import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import utc from 'dayjs/plugin/utc';

import {
  CurrentDropdownOption,
  DateTimeService,
  latestSevenDays,
  latestThirtyDays,
  latestNintyDays,
  latestFiftyTwoWeeks,
  currentMonth,
  priorMonth,
  currentQuarter,
  currentYear,
  custom,
  previousPeriod,
  previousPeriodSameWeekStart,
  previousYear,
  previousYearSameWeekStart,
  customCompare
} from './date-time.service';
import { SelectionService } from './selection.service';

dayjs.extend(utc);
dayjs.extend(quarterOfYear);

interface CompareDropdownOption {
  name: string;
  label?: string;
}

@Injectable({ providedIn: 'root' })
export class StandardDatePresetService {
  constructor(
    private dateTimeService: DateTimeService,
    private selectionService: SelectionService
  ) {}

  private get firstDayOfWeek(): number {
    return this.selectionService.getSelection().brand?.firstDayOfWeek ?? 0;
  }

  get currentRangeOptions(): CurrentDropdownOption[] {
    const maxDate = this.dateTimeService.maximumEndDate();
    return [
      {
        name: latestSevenDays,
        label: 'Last 7 days',
        action: this.lastNDays(maxDate, 7)
      },
      {
        name: latestThirtyDays,
        label: 'Last 30 days',
        action: this.lastNDays(maxDate, 30)
      },
      {
        name: latestNintyDays,
        label: 'Last 90 days',
        action: this.lastNDays(maxDate, 90)
      },
      {
        name: latestFiftyTwoWeeks,
        label: 'Last 52 weeks',
        action: this.last52Weeks(maxDate)
      },
      {
        name: 'This week (WTD)',
        label: 'This week (WTD)',
        action: this.thisWeek(maxDate)
      },
      {
        name: currentMonth,
        label: 'This month (MTD)',
        action: this.thisMonth(maxDate)
      },
      {
        name: currentQuarter,
        label: 'This quarter (QTD)',
        action: this.thisQuarter(maxDate)
      },
      {
        name: currentYear,
        label: 'This year (YTD)',
        action: this.thisYear(maxDate)
      },
      {
        name: 'Last week',
        label: 'Last week',
        action: this.lastWeek(maxDate)
      },
      {
        name: priorMonth,
        label: 'Last month',
        action: this.lastMonth(maxDate)
      },
      {
        name: 'Last quarter',
        label: 'Last quarter',
        action: this.lastQuarter(maxDate)
      },
      {
        name: 'Last year',
        label: 'Last year',
        action: this.lastYear(maxDate)
      },
      { name: custom, label: 'Custom', action: null }
    ];
  }

  get compareRangeOptions(): CompareDropdownOption[] {
    return [
      { name: previousPeriod, label: 'Previous period' },
      {
        name: previousPeriodSameWeekStart,
        label: 'Previous period (match day of week)'
      },
      { name: previousYear, label: 'Previous year' },
      {
        name: previousYearSameWeekStart,
        label: 'Previous year (match day of week)'
      },
      { name: customCompare, label: 'Custom' }
    ];
  }

  private weekStart(date: dayjs.Dayjs): dayjs.Dayjs {
    const diff = (date.day() - this.firstDayOfWeek + 7) % 7;
    return date.subtract(diff, 'day');
  }

  private lastNDays(maxDate: dayjs.Dayjs, n: number) {
    return {
      startDate: maxDate.subtract(n - 1, 'day'),
      endDate: maxDate
    };
  }

  private last52Weeks(maxDate: dayjs.Dayjs) {
    const lastDayOfPrevWeek = this.weekStart(maxDate).subtract(1, 'day');
    const firstDayOfPrevWeek = this.weekStart(lastDayOfPrevWeek);
    const startDay = firstDayOfPrevWeek.subtract(51, 'week');
    return { startDate: startDay, endDate: lastDayOfPrevWeek };
  }

  private thisWeek(maxDate: dayjs.Dayjs) {
    return { startDate: this.weekStart(maxDate), endDate: maxDate };
  }

  private thisMonth(maxDate: dayjs.Dayjs) {
    return { startDate: maxDate.startOf('month'), endDate: maxDate };
  }

  private thisQuarter(maxDate: dayjs.Dayjs) {
    return { startDate: maxDate.startOf('quarter'), endDate: maxDate };
  }

  private thisYear(maxDate: dayjs.Dayjs) {
    return { startDate: maxDate.startOf('year'), endDate: maxDate };
  }

  private lastWeek(maxDate: dayjs.Dayjs) {
    const lastDayOfPrevWeek = this.weekStart(maxDate).subtract(1, 'day');
    const firstDayOfPrevWeek = this.weekStart(lastDayOfPrevWeek);
    return { startDate: firstDayOfPrevWeek, endDate: lastDayOfPrevWeek };
  }

  private lastMonth(maxDate: dayjs.Dayjs) {
    const prev = maxDate.subtract(1, 'month');
    return { startDate: prev.startOf('month'), endDate: prev.endOf('month') };
  }

  private lastQuarter(maxDate: dayjs.Dayjs) {
    const prev = maxDate.subtract(1, 'quarter');
    return {
      startDate: prev.startOf('quarter'),
      endDate: prev.endOf('quarter')
    };
  }

  private lastYear(maxDate: dayjs.Dayjs) {
    const prev = maxDate.subtract(1, 'year');
    return { startDate: prev.startOf('year'), endDate: prev.endOf('year') };
  }
}
